import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TicketIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { ROLES, ROLE_COLORS, ROLE_ROUTES, ROLE_CTA } from './utils/roleUtils';

// Icon mapping by role
const ROLE_ICONS = {
  [ROLES.CONSUMER]: TicketIcon,
  [ROLES.BUSINESS]: ChartBarIcon,
  [ROLES.VENUE]: BuildingStorefrontIcon,
  [ROLES.ADMIN]: ShieldCheckIcon,
};

/**
 * Single Dashboard Button
 * Used when user has only one dashboard-worthy role
 */
function SingleDashboardButton({ to, title, subtitle, role, colors, badge, onClose }) {
  const Icon = ROLE_ICONS[role];
  
  return (
    <Link
      to={to}
      onClick={onClose}
      className={`block rounded-xl bg-gradient-to-r ${colors.gradient} p-4 mb-4 shadow-lg`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-display text-white">{title}</div>
          <div className="text-xs text-white/70">{subtitle}</div>
        </div>
        {badge !== null && badge !== undefined ? (
          <div className="px-2.5 py-1 bg-white/30 rounded-full">
            <span className="text-sm font-bold text-white">{badge}</span>
          </div>
        ) : (
          <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
    </Link>
  );
}

/**
 * Multi-Role Dashboard Buttons
 * Side-by-side buttons when user has multiple roles
 */
function MultiDashboardButtons({ roles, onClose }) {
  return (
    <div className={`grid gap-2 mb-4 ${roles.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
      {roles.map((role) => {
        const Icon = ROLE_ICONS[role];
        const colors = ROLE_COLORS[role];
        const route = ROLE_ROUTES[role];
        const cta = ROLE_CTA[role];
        
        return (
          <Link
            key={role}
            to={route}
            onClick={onClose}
            className={`rounded-xl bg-gradient-to-r ${colors.gradient} p-3 shadow-lg hover:scale-[1.02] transition-transform`}
          >
            <div className="flex flex-col items-center text-center gap-2">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="font-display text-white text-sm">{cta.shortLabel}</div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Primary Action CTA
 * 
 * Renders either:
 * - Single big button for users with one dashboard role
 * - Side-by-side buttons for users with multiple dashboard roles
 * - Consumer-style button for users with no special roles
 */
export default function MobileNavPrimaryAction({
  to,
  title,
  subtitle,
  role,
  colors,
  badge,
  onClose,
  dashboardRoles = [], // New prop: array of dashboard-worthy roles
}) {
  // If user has multiple dashboard roles, show side-by-side buttons
  if (dashboardRoles.length > 1) {
    return <MultiDashboardButtons roles={dashboardRoles} onClose={onClose} />;
  }
  
  // Otherwise show single button (original behavior)
  return (
    <SingleDashboardButton
      to={to}
      title={title}
      subtitle={subtitle}
      role={role}
      colors={colors}
      badge={badge}
      onClose={onClose}
    />
  );
}
